import { useState, type ReactNode } from 'react';
import { useT } from '@hana/i18n';
import { DockPanel, useDock } from '@hana/shell';
import { ADJ_ROWS, DCF_ROWS, INV_CUS, ISS_ROWS, QUE_ROWS } from './inv';
import './invdock.i18n';
import s from './ardock.module.css';

// Bảng phải của Mint-Invoice 1920 (dock 372): Phân tích · Việc · Duyệt — theo code board Mint-Invoice `dkAn` / `dkTask` / `dkAppr`. Chỉ đọc; nút ghi báo toast ở màn.
export type InvDockKey = 'an' | 'task' | 'appr';
export const useInvDock = () => useDock<InvDockKey>('an');

const nf = (x: number): string => x.toLocaleString('vi-VN', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const FL: readonly (readonly [string, number, string])[] = [['ivd.st.wait', 5, 'var(--d-amber)'], ['ivd.st.toIssue', 8, 'var(--d-blue)'], ['ivd.st.issued', 23, 'var(--ok)'], ['ivd.st.adj', 4, 'var(--d-violet)']];
const BM: readonly (readonly [string, string, string, string, string, string])[] = [
  ['ketoan@ruitongvn.com', 'RUITONGVN-SC', 'Đề nghị sửa THC trên DN26054305', 'Yêu cầu sửa', 'DN26054305', '08:42'],
  ['acc@nammy.vn', 'NAMMY', 'Đã kiểm, đồng ý debit DN26054311', 'Xác nhận', 'DN26054311', '09:15'],
  ['ap@tamngoc.vn', 'TAMNGOC', 'Xin bản PDF hoá đơn 1/C26THL/2418', 'Hỏi hoá đơn', '', '10:02'],
];
const TASKS: readonly (readonly [string, string, string, string, 'warn' | 'err' | 'lb'])[] = [
  ['DEB', 'Lập debit job SE260601450 (đã khoá cước 21/Oct)', 'Hôm nay 16:00', 'MAITTN', 'warn'],
  ['DCF', 'Nhắc khách xác nhận DN26054299 · lần nhắc 4 hôm nay', 'Hôm nay', 'HOATTN', 'warn'],
  ['INV', 'Phát hành HĐ DN26054288 · quá hạn 3 ngày', 'Quá hạn 3 ngày', 'LIENNTK', 'err'],
  ['DCF', 'Sửa debit DN26054305 theo yêu cầu khách', 'Mai 10:00', 'MAITTN', 'lb'],
  ['SOA', 'Gửi SOA tháng 10 cho Pacific Link', '31/Oct/2026', 'NGOCANH', 'lb'],
];
const WORK: readonly (readonly [string, number, string])[] = [
  ['ivd.w.todo', QUE_ROWS.filter((r) => r[8] === 'Chờ làm').length, 'var(--tx)'],
  ['ivd.w.doing', QUE_ROWS.filter((r) => r[8] === 'Đang làm').length, 'var(--link)'],
  ['ivd.w.waitConf', DCF_ROWS.length, 'var(--warn)'],
  ['ivd.w.doneToday', 2, 'var(--ok)'],
  ['ivd.w.recon', 2, 'var(--err)'],
];

export function InvDock({ dock, onNote, top }: { dock: ReturnType<typeof useInvDock>; onNote: () => void; top?: ReactNode }) {
  const t = useT();
  const [done, setDone] = useState<Readonly<Record<string, boolean>>>({});
  if (!dock.open) return null;
  const late = ISS_ROWS.filter((r) => r.days < 0).map((r) => ({ k: INV_CUS[r.cus]?.[0] ?? r.cus, v: r.net, d: t('ivd.days', { n: -r.days }) }));
  const lateMax = Math.max(...late.map((x) => x.v), 0.0001);
  const wait = ISS_ROWS.reduce((a, r) => a + r.net, 0);
  const dcfSum = DCF_ROWS.reduce((a, r) => a + r[3], 0);
  const N = FL.reduce((a, x) => a + x[1], 0);
  const base = (FL[0]?.[1] ?? 0) + (FL[1]?.[1] ?? 0) + (FL[2]?.[1] ?? 0);
  const doneN = FL[2]?.[1] ?? 0;
  const C = 2 * Math.PI * 52;
  let acc = 0;
  const arcs = FL.map(([k0, n, c]) => { const k = t(k0); const L = (n / N) * C; const g = L > 6 ? 3 : 0; const a = { k, n, c, dash: `${Math.max(L - g, 0).toFixed(1)} ${(C - L + g).toFixed(1)}`, off: (-acc).toFixed(1), p: `${Math.round((n / N) * 100)}%` }; acc += L; return a; });
  const pct = doneN / base;
  const kpi: readonly (readonly [string, string, string])[] = [[t('ivd.k.waitInv'), nf(wait), 'var(--tx)'], [t('ivd.k.lateIssue'), '3', 'var(--err)'], [t('ivd.k.issuedM'), '1.426,8', 'var(--tx)'], [t('ivd.k.waitConf'), nf(dcfSum), 'var(--tx)']];
  const appr = ADJ_ROWS.filter((a) => a[7] === 'Chờ KTT duyệt');
  const tabs = [{ key: 'an' as const, label: t('ivd.tab.an') }, { key: 'task' as const, label: t('ivd.tab.task'), n: TASKS.length + BM.length }, { key: 'appr' as const, label: t('ivd.tab.appr'), n: appr.length }];

  return (
    <DockPanel top={top} tabs={tabs} tab={dock.tab} onTab={dock.show} onClose={dock.close}>
      {dock.tab === 'an' && (
        <>
          <section className={s.sec}>
            <div className={s.hd}><h2>{t('ivd.month')}</h2><span>{t('ivd.unit')}</span></div>
            <div className={s.kpis}>
              {kpi.map(([k, v, c]) => <div key={k}><div>{k}</div><b style={{ color: c }}>{v}</b></div>)}
            </div>
            <div className={s.agRow} style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--bd)' }}>
              <svg role="img" aria-label={t('ivd.gaugeAria', { p: Math.round(pct * 100), d: doneN, b: base })} width="150" height="84" viewBox="0 0 190 104">
                <path d="M 15 95 A 80 80 0 0 1 175 95" fill="none" stroke="var(--line)" strokeWidth="14" strokeLinecap="round" />
                <path d="M 15 95 A 80 80 0 0 1 175 95" fill="none" stroke="var(--d-blue)" strokeWidth="14" strokeLinecap="round" strokeDasharray="251.3 251.3" strokeDashoffset={(251.3 * (1 - pct)).toFixed(1)} />
                <text x="95" y="84" textAnchor="middle" fontSize="28" fontWeight="550" fill="var(--tx)">{Math.round(pct * 100)}%</text>
              </svg>
              <div style={{ minWidth: 0 }}><div className={s.sub}>{t('ivd.gauge')}</div><b style={{ fontSize: 15, fontWeight: 'var(--fw-label)' }}>{doneN}/{base}</b><div className={s.sub}>{t('ivd.gaugeSub')}</div></div>
            </div>
          </section>
          <section className={s.sec}>
            <h2 className={s.h2}>{t('ivd.today')}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 6, marginBottom: 14 }}>
              {WORK.map(([k, n, c]) => (
                <button key={k} type="button" onClick={onNote} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, padding: 8, border: 0, borderRadius: 10, background: 'var(--card)', font: 'inherit', cursor: 'pointer', textAlign: 'left' }}>
                  <b style={{ fontSize: 18, fontWeight: 'var(--fw-label)', color: c }}>{n}</b><span style={{ fontSize: 11, color: 'var(--lb)', lineHeight: 1.25 }}>{t(k)}</span>
                </button>
              ))}
            </div>
            <h2 className={s.h2}>{t('ivd.byStep')}</h2>
            <div className={s.agRow}>
              <svg role="img" aria-label={t('ivd.byStepAria', { v: FL.map((x) => `${t(x[0])} ${x[1]}`).join(', ') })} width="108" height="108" viewBox="0 0 132 132">
                <circle cx="66" cy="66" r="52" fill="none" stroke="var(--line)" strokeWidth="16" />
                {arcs.map((a) => <circle key={a.k} cx="66" cy="66" r="52" fill="none" stroke={a.c} strokeWidth="16" strokeDasharray={a.dash} strokeDashoffset={a.off} transform="rotate(-90 66 66)" />)}
                <text x="66" y="68" textAnchor="middle" fontSize="20" fontWeight="550" fill="var(--tx)">{N}</text>
                <text x="66" y="84" textAnchor="middle" fontSize="11" fill="var(--lb)">{t('ivd.debit')}</text>
              </svg>
              <div className={s.leg} style={{ gap: 6 }}>
                {arcs.map((a) => <div key={a.k}><i style={{ background: a.c }} /><span>{a.k}</span><b>{a.n}</b><em>{a.p}</em></div>)}
              </div>
            </div>
          </section>
          <section className={s.sec}>
            <div className={s.hd}><h2>{t('ivd.lateIssue')}</h2><span className={s.err}>{t('ivd.debitN', { n: 3 })}</span></div>
            <div role="img" aria-label={t('ivd.lateAria', { v: late.map((x) => t('ivd.lateItem', { k: x.k, v: nf(x.v), d: x.d })).join('; ') })}>
              {late.map((a) => (
                <div key={a.k} style={{ display: 'grid', gridTemplateColumns: '104px minmax(0, 1fr) 46px 52px', alignItems: 'center', gap: 8, height: 30 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, color: 'var(--tx2)' }}>{a.k}</span>
                  <div className={s.track}><div style={{ width: `${Math.max(2, Math.round((a.v / lateMax) * 100))}%`, background: 'var(--warn)' }} /></div>
                  <b style={{ fontWeight: 'var(--fw-label)', textAlign: 'right' }}>{nf(a.v)}</b>
                  <span className={s.err} style={{ textAlign: 'right', fontSize: 12 }}>{a.d}</span>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
      {dock.tab === 'task' && (
        <>
          <section className={s.sec} aria-label={t('ivd.mailBox')} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div className={s.hd}><h2>billing@hanalogisticsvn.com</h2><span>{t('ivd.mailN', { n: BM.length })}</span></div>
            {BM.map(([from, cus, subj, tag, dn, at]) => (
              <div key={subj} style={{ padding: 10, borderRadius: 12, background: 'var(--card)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}><b style={{ flexGrow: 1, minWidth: 0, fontWeight: 'var(--fw-label)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subj}</b><span className={s.sub}>{at}</span></div>
                <div className={s.sub}><span className={s.code} style={{ fontWeight: 'var(--fw-label)' }}>{tag}</span> · {cus} · {from}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button type="button" onClick={onNote} className={s.act}>{t('ivd.toTask')}</button>
                  {dn && <button type="button" onClick={onNote} className={s.act}>{t('ivd.open', { dn })}</button>}
                  <button type="button" onClick={onNote} className={s.act}>{t('ivd.archive')}</button>
                </div>
              </div>
            ))}
          </section>
          {TASKS.map(([code, k, due, who, tone], i) => (
            <div key={k} className={s.task} style={{ opacity: done[k] ? 0.5 : 1 }}>
              <input type="checkbox" aria-label={t('ivd.done', { k })} checked={!!done[k]} onChange={() => setDone({ ...done, [k]: !done[k] })} />
              <div>
                <div className={s.tk} style={{ textDecoration: done[k] ? 'line-through' : 'none' }}>{k}</div>
                <div className={s.due}><b style={{ color: 'var(--link)', fontWeight: 'var(--fw-label)' }}>{code}</b> · <span className={i >= 0 && tone !== 'lb' ? s[tone] : ''}>{due}</span> · {who}</div>
              </div>
            </div>
          ))}
        </>
      )}
      {dock.tab === 'appr' && appr.map((a) => (
        <section key={a[0]} className={s.appr}>
          <div className={s.hd}><b>{a[0]} · {a[1]}</b><b className={a[5] < 0 ? s.err : ''}>{a[5] === 0 ? t('ivd.replace') : `${a[5] < 0 ? '−' : '+'}${nf(Math.abs(a[5]))} tr`}</b></div>
          <div className={s.tx2} style={{ fontSize: 12 }}>{t('ivd.origInv', { a: a[4], b: a[2] })}</div>
          <div className={s.sub}>{t('ivd.flow', { a: a[6] })}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" onClick={onNote} className={s.act}>{t('ivd.approve')}</button>
            <button type="button" onClick={onNote} className={s.act}>{t('ivd.return')}</button>
          </div>
        </section>
      ))}
    </DockPanel>
  );
}
